import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Factory, Cog, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const Manufacturing = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/manufacturing"]?.title} description={seoData["industries/manufacturing"]?.description} path="/industries/manufacturing" keywords={seoData["industries/manufacturing"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Manufacturing"
        description="Industrial coating and maintenance contractor for manufacturing facilities. Floor coatings, equipment repair, corrosion protection, and plant maintenance services for production environments across California."
        backgroundKey="manufacturing"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Production floor wear and chemical damage from heavy traffic and spills',
              'Equipment corrosion and mechanical wear from continuous operation',
              'Process piping leaks and corrosion under insulation (CUI)',
              'Concrete deterioration in processing and loading areas',
              'Secondary containment failures at chemical storage areas',
              'Unplanned downtime from equipment failures'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-slate-100 rounded-lg">
                <Factory className="text-slate-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-slate-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Cog className="text-slate-600 mr-3 flex-shrink-0" size={22} />
                  Industrial Floor Coating Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Heavy-duty resinous floor coatings for manufacturing environments. Epoxy, polyurethane, and MMA floor systems engineered for forklift traffic, chemical exposure, thermal cycling, and impact resistance. Safety striping and demarcation included.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-slate-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Cog className="text-slate-600 mr-3 flex-shrink-0" size={22} />
                  Equipment Repair & Protection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">In-situ repair of pumps, mixers, conveyors, and process equipment using Belzona epoxy composites. Bearing housing rebuild, shaft repair, and erosion protection to restore equipment to OEM specifications without replacement.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-slate-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Cog className="text-slate-600 mr-3 flex-shrink-0" size={22} />
                  Process Piping Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Cold-applied pipe repair and corrosion protection for process lines, cooling water, and utility piping. Composite wraps and epoxy repair systems for leak sealing without shutdowns or hot work.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-slate-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Cog className="text-slate-600 mr-3 flex-shrink-0" size={22} />
                  Concrete & Structural Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Floor slab repair, joint restoration, loading dock repair, and structural concrete rehabilitation. Epoxy injection for crack repair and protective coatings for long-term durability in production environments.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Manufacturing?</h2>
              <div className="space-y-3">
                {['Minimal production disruption — phased work and off-hours scheduling available',
                  'Fast-cure systems for time-sensitive production environments',
                  'No hot work — eliminate fire permits and safety shutdowns',
                  'Extend equipment service life and reduce capital expenditure',
                  'OSHA compliant repair methods with full safety documentation',
                  'Plant maintenance contractor with 25+ years manufacturing experience'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Plant Maintenance Partner</h3>
              <p className="text-slate-200 mb-6">BCS serves as a trusted maintenance contractor for manufacturing facilities across California. From routine coating maintenance to emergency equipment repair, we keep your production running.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/floor-repair')}>
                View Floor Coating Services <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Keep Your Manufacturing Facility Running</h2>
          <p className="text-blue-100 mb-8">Contact us for a plant maintenance assessment. We'll identify corrosion risks and recommend cost-effective repair and protection solutions.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Manufacturing;
